// ============================================
// Message List Component
// Displays messages with infinite scroll
// ============================================

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useChat } from './ChatProvider';
import MessageItem from './MessageItem';
import ReportMessageModal from './ReportMessageModal';
import { API_URL, getAnyValidToken } from '../../utils/authUtils';

/**
 * Scrollable message list with auto-scroll and load more
 */
const MessageList = ({ onSwitchToChannel }) => {
  const {
    messages,
    loadingMessages,
    hasMoreMessages,
    loadMoreMessages,
    currentUser,
    currentChannel,
    addReaction,
    editMessage,
    deleteMessage,
    pinMessage,
    unpinMessage,
    startDM,
    channels,
    setReplyingTo,
    fetchThreadReplies,
    threadReplies,
    threadReplyAlert,
    setThreadReplyAlert,
    highlightedMessageId
  } = useChat();

  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const prevMessagesLength = useRef(messages.length);
  const isNearBottom = useRef(true);

  // Check if user is near bottom of scroll
  const checkIfNearBottom = useCallback(() => {
    if (!containerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100;
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback((smooth = true) => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  }, []);

  // Handle scroll for load more
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    isNearBottom.current = checkIfNearBottom();

    // Load more when scrolled to top
    const { scrollTop } = containerRef.current;
    if (scrollTop < 50 && hasMoreMessages && !loadingMessages) {
      loadMoreMessages();
    }
  }, [hasMoreMessages, loadingMessages, loadMoreMessages, checkIfNearBottom]);

  // State for DM starting
  const [startingDM, setStartingDM] = useState(null);

  // State for message reporting
  const [reportingMessage, setReportingMessage] = useState(null);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Handle starting a DM from clicking on user avatar
  const handleStartDM = useCallback(async (userType, userId, displayName) => {
    if (!startDM || startingDM) return;

    setStartingDM(`${userType}:${userId}`);
    try {
      const result = await startDM(userType, userId, displayName);
      if (result && onSwitchToChannel) {
        // Find the DM channel in joined channels and switch to it
        const dmChannel = channels.joined?.find(ch => ch.id === result.channelId);
        if (dmChannel) {
          onSwitchToChannel(dmChannel);
        }
      }
    } finally {
      setStartingDM(null);
    }
  }, [startDM, startingDM, channels.joined, onSwitchToChannel]);

  // Handle replying to a message
  const handleReply = useCallback((message) => {
    setReplyingTo(message);
  }, [setReplyingTo]);

  // Handle viewing thread replies
  const handleViewThread = useCallback((messageId) => {
    fetchThreadReplies(messageId);
  }, [fetchThreadReplies]);

  // Handle opening report modal
  const handleOpenReport = useCallback((message) => {
    setReportingMessage(message);
  }, []);

  // Handle report submission
  const handleSubmitReport = useCallback(async (reportData) => {
    const token = getAnyValidToken();
    if (!token) {
      alert('You must be logged in to report messages');
      return;
    }

    setIsSubmittingReport(true);
    try {
      const response = await fetch(`${API_URL}/api/alumni/report-content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        setReportingMessage(null);
        alert('Thank you for your report. Our moderation team will review it.');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to submit report. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please check your connection and try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  }, []);

  // Auto-scroll on new messages (if near bottom)
  useEffect(() => {
    if (messages.length > prevMessagesLength.current) {
      // New message added
      if (isNearBottom.current) {
        scrollToBottom();
      }
    }
    prevMessagesLength.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Initial scroll to bottom
  useEffect(() => {
    scrollToBottom(false);
  }, []);

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });

    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  // Show centered loading state for initial channel load
  if (loadingMessages && messages.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
        <p className="text-sm">Loading messages...</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="h-full overflow-y-auto px-3 py-2 relative"
    >
      {/* Thread reply toast notification */}
      {threadReplyAlert && (
        <div className="sticky top-0 z-10 mb-2 animate-pulse">
          <div className="flex items-center justify-between bg-blue-500 text-white px-3 py-2 rounded-lg shadow-lg">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <span className="text-sm font-medium">
                {threadReplyAlert.senderName} replied to a thread
              </span>
            </div>
            <button
              onClick={() => setThreadReplyAlert(null)}
              className="p-1 hover:bg-blue-600 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Loading indicator at top for loading more messages */}
      {loadingMessages && messages.length > 0 && (
        <div className="flex justify-center py-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Load more button */}
      {hasMoreMessages && !loadingMessages && (
        <div className="flex justify-center py-2">
          <button
            onClick={loadMoreMessages}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            Load older messages
          </button>
        </div>
      )}

      {/* Empty state */}
      {messages.length === 0 && !loadingMessages && (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-sm">No messages yet</p>
          <p className="text-xs">Be the first to say something!</p>
        </div>
      )}

      {/* Messages grouped by date */}
      {Object.entries(groupedMessages).map(([date, dateMessages]) => (
        <div key={date}>
          {/* Date separator */}
          <div className="flex items-center my-3">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-3 text-xs text-gray-400 font-medium">{date}</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          {/* Messages for this date */}
          {dateMessages.map((message, index) => {
            const prevMessage = index > 0 ? dateMessages[index - 1] : null;
            const showSender = !prevMessage ||
              prevMessage.sender?.user_id !== message.sender?.user_id ||
              prevMessage.sender?.user_type !== message.sender?.user_type;

            const isOwnMessage = currentUser &&
              message.sender?.user_id === currentUser.id &&
              message.sender?.user_type === currentUser.type;

            const isAdmin = currentUser?.type === 'admin';

            return (
              <MessageItem
                key={message.id}
                message={message}
                showSender={showSender}
                isOwnMessage={isOwnMessage}
                isAdmin={isAdmin}
                isHighlighted={highlightedMessageId === message.id}
                isDM={currentChannel?.is_dm || false}
                onReaction={addReaction}
                onStartDM={handleStartDM}
                onEdit={editMessage}
                onDelete={deleteMessage}
                onPin={pinMessage}
                onUnpin={unpinMessage}
                onReply={handleReply}
                onViewThread={handleViewThread}
                threadReplies={threadReplies[message.id]}
                onReport={handleOpenReport}
              />
            );
          })}
        </div>
      ))}

      {/* Scroll anchor */}
      <div ref={bottomRef} />

      {/* Report Message Modal */}
      <ReportMessageModal
        isOpen={!!reportingMessage}
        onClose={() => setReportingMessage(null)}
        onSubmit={handleSubmitReport}
        message={reportingMessage}
        isSubmitting={isSubmittingReport}
      />
    </div>
  );
};

export default MessageList;
